import { ButtonsGroupLabelled } from 'components/shared/ButtonsGroup/buttons-group-labelled';
import { useTranslateForm } from 'hooks/use-translate-payoff';
import { ToggleFieldData, ToggleFieldMetadata } from 'models/forms/fields';
import { CheckboxFieldData, CheckboxFieldMetadata } from 'models/forms/fields/CheckboxFieldMetadata';
import { useCallback, useMemo } from 'react';
import { ErrorWrapper } from 'routes/essential-workspace/components/form/error-wrapper';
import { useFormErrors } from 'routes/essential-workspace/components/form/use-form-errors';
import { useFormFieldData } from 'routes/essential-workspace/components/form/use-form-field-data';

type BooleanFieldValues = 'Yes' | 'No';

export function BooleanField({
  id,
  translationKey,
  isReadOnly,
  isNullable,
}: ToggleFieldMetadata | CheckboxFieldMetadata) {
  const [value, setFormData] = useFormFieldData<CheckboxFieldData | ToggleFieldData>(id);

  const { getFieldError } = useFormErrors();
  const fieldError = getFieldError(id);

  const { translateLabel } = useTranslateForm();
  const label = translateLabel(translationKey ?? id);

  const formattedValue = useMemo<Nullable<BooleanFieldValues>>(() => {
    if (value === null) {
      return null;
    }
    return value ? 'Yes' : 'No';
  }, [value]);

  const options = useMemo<BooleanFieldValues[]>(() => ['Yes', 'No'], []);

  const handleOnValueChanged = useCallback(
    (value: readonly BooleanFieldValues[]) => {
      if (value.length === 0) {
        setFormData(null);
        return;
      }
      const formattedValue = value[0] === 'Yes' ? true : false;
      setFormData(formattedValue);
    },
    [setFormData],
  );

  return (
    <ErrorWrapper error={fieldError}>
      <ButtonsGroupLabelled<BooleanFieldValues>
        id={id}
        label={label}
        options={options}
        value={formattedValue}
        onValueChanged={handleOnValueChanged}
        isReadOnly={isReadOnly}
        isInvalid={fieldError?.severity === 'Error'}
        isHighlighted={fieldError?.severity === 'Warning'}
        isNullable={isNullable}
      />
    </ErrorWrapper>
  );
}
